import { useState, useEffect, useCallback, type ReactNode, type CSSProperties } from 'react'
import { Link } from 'react-router'
import { useTheme } from '../theme/ThemeProvider.tsx'
import { Search, Sun, Moon, Menu, Swords, UserPlus, Maximize, Minimize } from 'lucide-react'
import { UserMenu, type UserMenuUser, type UserMenuOrg, type UserMenuTeam } from '../user-menu/UserMenu.tsx'
import { SearchOverlay } from '../search-overlay/SearchOverlay.tsx'
import { useSearchOverlay, searchShortcutHint } from '../search-overlay/useSearchOverlay.ts'
import type { SearchResultItem, SearchResultCategory } from '../search-overlay/mock-data.ts'
import styles from './AppBar.module.css'

/** What AppBar passes to `renderSearch`. */
export interface AppBarSearchSlot {
  open: boolean
  onClose: () => void
  query: string
  onQueryChange: (query: string) => void
}

/** The signed-in viewer, as the account menu shows them. See UserMenu. */
export type AppBarUser = UserMenuUser

/** One of the viewer's organizations in the account menu. See UserMenu. */
export type AppBarOrg = UserMenuOrg

/** One of the viewer's teams in the account menu. See UserMenu. */
export type AppBarTeam = UserMenuTeam

export interface AppBarProps {
  onMenuToggle: () => void
  /** Keep the hamburger menu button visible at all widths (default: mobile only). */
  alwaysShowMenu?: boolean
  /**
   * Control placed at the very start of the bar, before the hamburger and the
   * brand — a sidebar collapse toggle, typically.
   *
   * Separate from `onMenuToggle` on purpose: on a phone that button opens the
   * sidebar as an overlay drawer, while on a desktop the sidebar is docked and
   * the equivalent gesture collapses it to a rail. They are two behaviours, so
   * they are two controls, each shown at the width where it makes sense —
   * rather than one button branching on the viewport at runtime.
   */
  leading?: ReactNode
  brand: ReactNode
  /** Results for the classic search overlay. Ignored when renderSearch is set. */
  getSearchCategories?: (query: string) => SearchResultCategory[] | Promise<SearchResultCategory[]>
  onSearchResultClick?: (item: SearchResultItem) => void
  searchPlaceholder?: string
  /**
   * Replaces the classic overlay with your own search — the Carousel Index.
   * AppBar still owns the trigger, the shortcuts and the open state, and
   * hands them over here; render nothing while `open` is false.
   */
  renderSearch?: (search: AppBarSearchSlot) => ReactNode
  competeUrl?: string
  /**
   * What the Carousel call-to-action says. `compete` (default) is finding a
   * competition; `join` is a club page asking a visitor in. Same place, same
   * Carousel colours either way.
   */
  competeKind?: 'compete' | 'join'
  actions?: ReactNode
  style?: CSSProperties
  user?: AppBarUser | null
  organizations?: AppBarOrg[]
  /** The viewer's teams, listed in the account menu. */
  teams?: AppBarTeam[]
  createOrgUrl?: string
  onLogout?: () => void
  profileUrl?: string
  /** The player's own stats page, in the account menu under Profile. */
  statsUrl?: string
  settingsUrl?: string
  /** The account menu's name for the settings link. Default "Settings". */
  settingsLabel?: string
  menuExtra?: ReactNode
}

/**
 * Whether the page is fullscreen, kept in step with the browser — Esc and the
 * browser's own controls leave fullscreen without going through the button.
 */
function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement)
  const supported = typeof document !== 'undefined' && !!document.fullscreenEnabled

  useEffect(() => {
    if (!supported) return
    const sync = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [supported])

  const toggle = useCallback(() => {
    const done = document.fullscreenElement
      ? document.exitFullscreen()
      : document.documentElement.requestFullscreen()
    done.catch(() => undefined)
  }, [])

  return { supported, isFullscreen, toggle }
}

export function AppBar({
  onMenuToggle,
  alwaysShowMenu,
  leading,
  brand,
  getSearchCategories,
  onSearchResultClick,
  searchPlaceholder = 'Search',
  renderSearch,
  competeUrl,
  competeKind = 'compete',
  actions,
  style,
  user,
  organizations,
  teams,
  createOrgUrl = '/create-org',
  onLogout,
  profileUrl = '/profile',
  statsUrl,
  settingsUrl = '/settings',
  settingsLabel,
  menuExtra,
}: AppBarProps) {
  const { theme, toggleTheme } = useTheme()
  const fullscreen = useFullscreen()
  const { open, query, setQuery, openSearch, closeSearch, triggerRef } = useSearchOverlay()
  const [categories, setCategories] = useState<SearchResultCategory[]>([])

  useEffect(() => {
    if (renderSearch || !getSearchCategories) return
    let cancelled = false
    const result = getSearchCategories(query)
    if (result instanceof Promise) {
      result.then((cats) => { if (!cancelled) setCategories(cats) })
    } else {
      setCategories(result)
    }
    return () => { cancelled = true }
  }, [getSearchCategories, renderSearch, query])

  return (
    <>
      <header className={styles.header} style={style}>
        <div className={styles.left}>
          {leading}
          <button
            className={[styles.menuBtn, alwaysShowMenu && styles.menuBtnVisible].filter(Boolean).join(' ')}
            onClick={onMenuToggle}
            aria-label="Toggle sidebar"
          >
            <Menu size={20} />
          </button>
          {brand}
        </div>

        <button ref={triggerRef} className={styles.searchTrigger} onClick={openSearch}>
          <Search size={15} />
          <span className={styles.searchText}>{searchPlaceholder}</span>
          <kbd className={styles.searchKbd}>{searchShortcutHint()}</kbd>
        </button>

        <div className={styles.right}>
          <button
            className={styles.searchIconBtn}
            onClick={openSearch}
            aria-label="Search"
          >
            <Search size={18} />
          </button>
          {competeUrl && (
            <Link to={competeUrl} className={styles.competeBtn}>
              {competeKind === 'join' ? <UserPlus size={18} /> : <Swords size={18} />}
              <span>{competeKind === 'join' ? 'Join' : 'Compete'}</span>
            </Link>
          )}
          {actions && <div className={styles.actions}>{actions}</div>}
          {/* Every bar ends the same way: fullscreen, theme, account. */}
          {fullscreen.supported && (
            <button
              className={styles.fullscreenBtn}
              onClick={fullscreen.toggle}
              aria-label={fullscreen.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              title={fullscreen.isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {fullscreen.isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
          )}

          <button
            className={styles.themeToggleBtn}
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <UserMenu
            user={user}
            organizations={organizations}
            teams={teams}
            menuExtra={menuExtra}
            createOrgUrl={createOrgUrl}
            profileUrl={profileUrl}
            statsUrl={statsUrl}
            settingsUrl={settingsUrl}
            settingsLabel={settingsLabel}
            onLogout={onLogout}
          />
        </div>
      </header>

      {renderSearch ? (
        renderSearch({ open, onClose: closeSearch, query, onQueryChange: setQuery })
      ) : (
        <SearchOverlay
          open={open}
          onClose={closeSearch}
          query={query}
          onQueryChange={setQuery}
          categories={categories}
          onResultClick={onSearchResultClick}
        />
      )}
    </>
  )
}
